import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Header } from "../components/shared";
import Footerr from "../components/Footerr/Footerr";
import { caseStudiesData, caseStudyCategories } from "../../data/caseStudiesData";
import { ArrowRight, TrendingUp, Clock, MapPin, Quote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import usePageMeta from "../../seo/usePageMeta";
import { useRegion } from "../../regions/RegionContext";

const CaseStudiesPage = () => {
  const { config } = useRegion();
  usePageMeta({
    title: "Case Studies — Real Restaurant Transformations | Terces Solutions",
    description: config.content.caseStudiesIntro,
    path: "/CaseStudies",
  });

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [filteredCases, setFilteredCases] = useState(caseStudiesData);
  const navigate = useNavigate();

  useEffect(() => {
    // Enable smooth scrolling
    document.documentElement.style.scrollBehavior = 'smooth';

    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  useEffect(() => {
    if (selectedCategory === "All") {
      setFilteredCases(caseStudiesData);
    } else {
      setFilteredCases(
        caseStudiesData.filter((study) => study.category === selectedCategory)
      );
    }
  }, [selectedCategory]);

  return (
    <div className="case-studies-page bg-white">
      <Header />
      <main>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden" style={{ backgroundColor: "#F6F4EF" }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-[#1b7840] text-sm font-bold uppercase tracking-wider mb-6">
              Success Stories
            </p>
            <div className="inline-block relative">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 leading-tight">
                Case Studies
              </h1>
              <motion.svg
                className="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
                width="240"
                height="12"
                viewBox="0 0 240 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <path
                  d="M2 10C50 2, 190 2, 238 10"
                  stroke="#1b7840"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </motion.svg>
            </div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mt-8">
              {config.content.caseStudyRegionNote}{" "}
              See how we've helped businesses like yours achieve remarkable results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 px-6 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {caseStudyCategories.map((category) => (
              <motion.button
                key={category}
                onClick={() => setSelectedCategory(category)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  selectedCategory === category
                    ? "bg-gradient-to-r from-[#1b7840] to-[#145c32] text-white shadow-lg shadow-[#1b7840]/20"
                    : "bg-white text-gray-700 hover:bg-[#F6F4EF] border-2 border-gray-200 hover:border-[#1b7840]/30"
                }`}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredCases.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl hover:border-[#1b7840]/30 transition-all duration-300 group"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/95 backdrop-blur-sm text-[#1b7840] px-4 py-2 rounded-xl font-bold text-sm shadow-lg">
                      {study.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#1b7840] transition-colors">
                    {study.title}
                  </h3>

                  {/* Client Info */}
                  <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#1b7840]" />
                      <span>{study.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#1b7840]" />
                      <span>{study.timeline}</span>
                    </div>
                  </div>

                  {/* Challenge */}
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-[#1b7840] uppercase tracking-wider mb-2">
                      Challenge
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {study.challenge}
                    </p>
                  </div>

                  {/* Results Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {Object.entries(study.results).map(([key, value]) => (
                      <div
                        key={key}
                        className="bg-gradient-to-br from-[#F6F4EF] to-white p-4 rounded-xl border border-[#1b7840]/10"
                      >
                        <div className="flex items-start gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-[#1b7840] mt-1" />
                          <div>
                            <p className="text-2xl font-bold text-[#1b7840]">
                              {value}
                            </p>
                            <p className="text-xs text-gray-600 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Services Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {study.services.map((service, idx) => (
                      <span
                        key={idx}
                        className="bg-[#1b7840]/10 text-[#1b7840] px-3 py-1 rounded-lg text-xs font-semibold"
                      >
                        {service}
                      </span>
                    ))}
                  </div>

                  {/* Testimonial */}
                  <div className="bg-gradient-to-br from-[#F6F4EF]/50 to-white p-6 rounded-xl border border-[#1b7840]/10 relative">
                    <Quote className="absolute top-4 right-4 w-8 h-8 text-[#1b7840]/20" />
                    <p className="text-gray-700 italic mb-4 relative z-10">
                      "{study.testimonial.quote}"
                    </p>
                    <div>
                      <p className="font-bold text-gray-900">
                        {study.testimonial.author}
                      </p>
                      <p className="text-sm text-gray-600">
                        {study.testimonial.position}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* No Results */}
          {filteredCases.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="text-xl text-gray-600">
                No case studies found for this category.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#F6F4EF] to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Let's transform your business with proven strategies and expert execution.
            </p>
            <button
              onClick={() => navigate("/Contact")}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#1b7840] to-[#145c32] text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-[#1b7840]/30 transition-all transform hover:scale-105"
            >
              Start Your Transformation
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>
      </main>

      <Footerr />
    </div>
  );
};

export default CaseStudiesPage;
